import { RotateCcw } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

import { useResetCertification } from '../hooks/useResetCertification';
import { CertificationResetButtonProps } from '../types';

/**
 * Button component to reset certification progress
 * Isolated in the read-only module for future admin-only access
 */
export const CertificationResetButton = ({
  userId,
  caseId,
  className,
  compact = false,
}: CertificationResetButtonProps) => {
  const { resetCertification, isResetting } = useResetCertification();
  const { toast } = useToast();

  const handleReset = async () => {
    const success = await resetCertification(userId, caseId);

    if (success) {
      toast({
        title: 'Certification reset',
        description: 'All certification attempts have been deleted.',
      });
    } else {
      toast({
        title: 'Error',
        description: 'Failed to reset certification. Please try again.',
        variant: 'destructive',
      });
    }
  };

  if (compact) {
    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            disabled={isResetting}
            className={cn(
              'h-auto gap-1.5 px-2 py-1 text-muted-foreground hover:text-foreground',
              className
            )}
          >
            <RotateCcw className={cn('h-3.5 w-3.5', isResetting && 'animate-spin')} />
            <span className="hidden text-xs sm:inline">Reset</span>
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Certification Progress</AlertDialogTitle>
            <AlertDialogDescription>
              Reset the counter to unlock 3 more certification attempts.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleReset()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" disabled={isResetting} className={cn('gap-2', className)}>
          <RotateCcw className={cn('h-4 w-4', isResetting && 'animate-spin')} />
          Reset Certification
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reset Certification Progress</AlertDialogTitle>
          <AlertDialogDescription>
            Reset the counter to unlock 3 more certification attempts.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => void handleReset()}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Reset
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
